import $ from 'jquery'
import _ from 'lodash'
import icon from 'scoreboard/components/icon'
import HoverActions from 'scoreboard/components/hover-actions'
/**
 * View is for trees that require clicking a select button in
 * a node in order to select/add the node. (example: org-pane)
 *
 * Usage:
 *
 *	this.treeActions = new TreeActions({
 *		el: this.el,
 *		tree: this.gardener.tree,
 *		buttonContent: i18n.get('common.add'),
 *		nodesReselectable: true,
 *		permissions: true,
 *		extraClass: 'extra-class'
 *	}).render()
 *
 *	// When options is clicked, call the select/add method within the view
 *	this.treeActions.on('selected', this.select.bind(this))
 *
 * @param {Object} options Configuration options for TreeActions
 * @param {HTMLElement} options.el Attach the tree to the view
 * @param {Object} options.tree Pass the tree to the view
 * @param {string} options.buttonContent Set the button text (e.g., Add)
 * @param {boolean} [options.nodesReselectable=false] Allows nodes to be selected more than once
 * @param {boolean} [options.permissions=false] Applies styling based off of object permissions
 * @param {boolean} [options.disableTreeSelection=false] Disables selecting a tree node when the tree action is clicked.
 * @param {string} [options.extraClass] Additional class(es) to apply to the button
 *
 * @fires selected
 * This event is triggered when a user clicks the select button on a node that
 * can be added, passing the parent ID of the selected node.
 *
 * @fires selectionDisabled
 * Triggered when a node's select button is clicked but is disabled.
 * It passes the `event.currentTarget`.
 *
 * @fires treeActionReset
 * This event is triggered when a node has been clicked or selected.
 * Passes the selected node ID.
 *
 */

var TreeActions = HoverActions.extend({

	initialize: function(options) {
		HoverActions.prototype.initialize.apply(this, arguments)

		this.setTree()
		this.nodesReselectable = _.isNil(this.options.nodesReselectable) ? false : true

		if (this.isCheckingPermissions()) {
			this.on('treeActionReset', () => this.checkPermissions())
				.on('nodeActivated', (node) => this.checkPermissions(node))
		}

		this.$el.addClass(options.extraClass)

		this.showActions()
	},

	/*
	 * Sets which tree to use. Called in initialize() and can be called again to
	 * reset. Example: this.treeActions.setTree(this.gardener.tree)
	 */
	setTree(tree) {
		this.tree = tree || this.options.tree
	},

	/*
	 * Called in order to 'select' or load a chosen node by clicking the 'Select'
	 * button inside the .label-mask.
	 */
	select: function(e) {
		e.stopPropagation() // Prevent the tree's click handler from firing

		// if not addable, don't trigger anything
		if ($(e.currentTarget).closest('.addable').length == 0 || $(e.currentTarget).hasClass('has-no-tree-action')) {
			return
		}

		// Only select if button isn't disabled
		let target = $(e.currentTarget)
		let nodeId = target.closest('li.node').data('id')
		if (!target.hasClass('is-disabled')) {
			this.parentNodeId = nodeId
			// If this.options.disableTreeSelection is true, don't make a
			// tree selection. The default is to allow a tree selection.
			if (!this.options.disableTreeSelection) {
				this.tree.select(this.parentNodeId, {
					toggleOnSelect: false
				})
			}
			this.trigger('selected', this.parentNodeId)
			this.resetTreeAction()
		} else if (!target.hasClass('is-checked')) {
			let treeNodeData = this.tree.get(nodeId)
			let tooltip = this.options.disabledClickTooltip || _.stubTrue
			tooltip($(e.currentTarget), treeNodeData)
			this.trigger('selectionDisabled', $(e.currentTarget))
		}
	},

	/*
	 * Updates this.selectedNodeId after a node has been clicked.
	 */
	resetTreeAction: function() {
		var id = this.$('.node.selected').data('id')
		// Issue #15728: "nodesReselectable" option
		// If a node has been added, it is disabled. If a different node is clicked after,
		// enable the previously disabled node it so the user can add the same node again if desired.
		if (this.nodesReselectable && !_.isNil(this.selectedNodeId) && this.selectedNodeId !== id) {
			this.removeCheckmark(this.selectedNodeId)
		}
		// set/reset selectedNodeId
		this.selectedNodeId = id
		this.trigger('treeActionReset', this.selectedNodeId)
	},

	/*
	 * When a node is a clicked, reset the selected node ID and give the
	 * selected node's button full opacity.
	 */
	nodeFocused: function() {
		this.resetTreeAction()
		this.buttonFullOpacity()
	},

	/*
	 * Checks if the tree included permissions data or the options defined a
	 * permission check
	 */
	isCheckingPermissions () {
		return this.options.permissions || _.isFunction(this.options.itemPermissible)
			|| _.isFunction(this.options.itemSelectable)
	},

	/**
	 * If permissions was enabled in the options, check to see if the node can be manipulated, if not shows a locked
	 * icon
	 * @param {JQuery} [node] If null uses the node with the ".selected" class applied
	 */
	checkPermissions (node) {
		if (!this.isCheckingPermissions()) return

		let activeNode = node || this.$('.node.selected')
		if (!_.isEmpty(activeNode)) {
			let itemPermissible = this.options.itemPermissible || _.stubTrue
			let itemSelectable = this.options.itemSelectable || _.stubTrue
			let treeNodeData = this.tree.get(activeNode.data('id'))
			if (treeNodeData && (treeNodeData.view === false || !itemPermissible(treeNodeData))) {
				activeNode.addClass('is-locked')
					.find('.label-mask')
					.not('.has-no-tree-action') // Don't append extra icon if class is already applied
					.addClass('has-no-tree-action')
					//even though it is pre-fixed "org" this really could be applied to any type of object
					.append(`<span class="no-action-icon">${icon('org-locked')}</span>`)
			} else if (!itemSelectable(treeNodeData)) {
				activeNode.find('.label-mask').addClass('is-disabled')
			}
		}
	},

	/*
	 * Hides the tree-actions functionality in situations when you can toggle
	 * between needing and not needing the tree-actions. Such as going between
	 * edit and view modes.
	 */
	hideActions: function() {
		this.$el.addClass('is-hiding-tree-actions')
	},

	/*
	 * Show the tree actions functionality after it has been hidden
	 * by hideActions()
	 */
	showActions: function() {
		this.$el.removeClass('is-hiding-tree-actions')
	}

})

export default TreeActions


TreeActions.__filename__ = 'components/tree-actions.js'
